import { useEffect } from 'react';

// Height of the area the user can actually see, published as a custom
// property so a page shell can be sized from it.
export const APP_HEIGHT_VARIABLE = '--app-visible-height';

// Set on <html> while a page is locked, so a section can take the page's
// height without changing how that same section behaves elsewhere.
export const LOCKED_CLASS = 'app-viewport-locked';

// Set on the box a page's body sections scroll in.
export const PAGE_BODY_CLASS = 'app-page-body';

// Makes a wrapper between that box and a section a flex column that takes
// the remaining height on a locked page, so the section can fill it.
export const fillSx = {
  [`html.${LOCKED_CLASS} .${PAGE_BODY_CLASS} &`]: { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
};

// Pins a page to the visual viewport for as long as the component using
// this is mounted.
//
// A mobile browser keeps two heights: the layout viewport, which it grows
// and shrinks as its own toolbar hides and reappears, and the visual
// viewport, which is what the user sees. A document that can scroll even
// by one pixel lets a drag hide that toolbar, so the layout viewport is
// resized in the middle of the gesture and whatever the browser had
// already composited is left at the offset it had before the resize --
// the page painted a few hundred pixels down, with a band above it.
//
// Locking the document (it never scrolls, so the toolbar never hides) and
// sizing the shell from visualViewport instead of a percentage of the
// layout viewport removes both halves of that: there is no gesture the
// browser can answer with a resize, and the shell's height is the height
// the user sees, keyboard open or not.
const useViewportLock = () => {
  useEffect(() => {
    const root = document.documentElement;
    const { body } = document;
    const previous = {
      scrollBehavior: root.style.scrollBehavior,
      overscrollBehaviorY: root.style.overscrollBehaviorY,
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      appHeight: root.style.getPropertyValue(APP_HEIGHT_VARIABLE),
    };

    // the shared theme sets scroll-behavior: smooth on <html> for anchor
    // scrolls; some WebKit versions also animate touch-driven scrolls with it
    root.style.scrollBehavior = 'auto';
    root.style.overscrollBehaviorY = 'none';
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    root.classList.add(LOCKED_CLASS);

    const { visualViewport } = window;

    const apply = () => {
      const height = visualViewport ? visualViewport.height : window.innerHeight;
      root.style.setProperty(APP_HEIGHT_VARIABLE, `${Math.round(height)}px`);
      // a document that was already scrolled when this mounted, or that the
      // browser scrolled itself, would stay offset by its toolbar's height
      if (document.scrollingElement) {
        document.scrollingElement.scrollTop = 0;
      }
    };

    apply();
    window.addEventListener('resize', apply);
    window.addEventListener('orientationchange', apply);
    if (visualViewport) {
      visualViewport.addEventListener('resize', apply);
      visualViewport.addEventListener('scroll', apply);
    }

    return () => {
      window.removeEventListener('resize', apply);
      window.removeEventListener('orientationchange', apply);
      if (visualViewport) {
        visualViewport.removeEventListener('resize', apply);
        visualViewport.removeEventListener('scroll', apply);
      }
      root.style.scrollBehavior = previous.scrollBehavior;
      root.style.overscrollBehaviorY = previous.overscrollBehaviorY;
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.bodyOverflow;
      root.classList.remove(LOCKED_CLASS);
      if (previous.appHeight) {
        root.style.setProperty(APP_HEIGHT_VARIABLE, previous.appHeight);
      } else {
        root.style.removeProperty(APP_HEIGHT_VARIABLE);
      }
    };
  }, []);
};

export default useViewportLock;
